import { Button, Checkbox, Col, Divider, Row, Table, TableProps } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useAdministrationPersonContext } from '../../containers/AdministrationPersonContainer/AdministrationPersonContext';
import { TransferIcon } from '../../lib/img/TransferIcon';
import { IPermissionTableRecord } from '../../lib/interfaces/Administration';
import { checkIsLoading } from '../../lib/utils/checkLoadingStatus';
import { hasAccess } from '../../lib/utils/usePermissions';
import { useAppSelector } from '../../store/hooks';

interface IProps {}

export const AdministrationPersonPermisionsAdd: FC<IProps> = () => {
  const { onSubmitAssignPermissions } = useAdministrationPersonContext();
  const { userLoading, user, allPermissions, allPermissionsLoading } = useAppSelector((state) => state.administration);
  const [editMode, setEditMode] = useState(false);

  const [assignedTableData, setAssignedTableData] = useState<IPermissionTableRecord[]>([]);

  const [availableTableData, setAvailableTableData] = useState<IPermissionTableRecord[]>([]);

  // console.log({ allPermissions });
  // console.log({ availableTableData });

  const setDefaultAssignedTableData = () => {
    setAssignedTableData(
      (user?.includedPermissions || [])
        .map(({ id, dsc }) => ({
          checked: false,
          id,
          key: id,
          permissionName: dsc,
        }))
        .sort((a, b) => (a.permissionName > b.permissionName ? 1 : -1))
    );
  };

  const setDefaultAvailableTableData = () => {
    const includedRolePermissionsIds = (user?.includedRoles[0]?.includedPermissions || []).map(({ id }) => id);
    const includedPermissionsIds = (user?.includedPermissions || []).map(({ id }) => id);
    setAvailableTableData(
      allPermissions
        .map(({ id, dsc }) => ({
          checked: false,
          id,
          key: id,
          permissionName: dsc,
        }))
        .filter(({ id }) => !includedRolePermissionsIds.includes(id))
        .filter(({ id }) => !includedPermissionsIds.includes(id))
        .sort((a, b) => (a.permissionName > b.permissionName ? 1 : -1))
    );
  };

  // Установка исходного списка назначенных привилегий
  useEffect(() => {
    setDefaultAssignedTableData();
  }, [user]);

  // Установка исходного списка доступных привилегий
  useEffect(() => {
    setDefaultAvailableTableData();
  }, [allPermissions, user]);

  const columnsAssigned = [
    {
      key: 0,
      dataIndex: 'checked',
      render: (checked) => editMode && <Checkbox checked={checked} />,
      width: '28px',
    },
    {
      key: 1,
      dataIndex: 'permissionName',
    },
  ];

  const columnsAvailable = [
    {
      key: 0,
      dataIndex: 'checked',
      render: (checked) => editMode && <Checkbox checked={checked} />,
      width: '28px',
    },
    {
      key: 1,
      dataIndex: 'permissionName',
    },
  ];

  const handleDiscard = () => {
    setDefaultAssignedTableData();
    setDefaultAvailableTableData();
    setEditMode(false);
  };

  const toggleCheckAssigned = (id: number) => {
    setAssignedTableData((prev) =>
      prev.map((prevItem) => (prevItem.id === id ? { ...prevItem, checked: !prevItem.checked } : prevItem))
    );
  };

  const toggleCheckAssignedAll = () => {
    const currentChecked = assignedTableData.filter(({ checked }) => checked).length === assignedTableData.length;
    setAssignedTableData((prev) => prev.map((prevItem) => ({ ...prevItem, checked: !currentChecked })));
  };

  const toggleCheckAvailable = (id: number) => {
    setAvailableTableData((prev) =>
      prev.map((prevItem) => (prevItem.id === id ? { ...prevItem, checked: !prevItem.checked } : prevItem))
    );
  };

  const toggleCheckAvailableAll = () => {
    const currentChecked = availableTableData.filter(({ checked }) => checked).length === availableTableData.length;
    setAvailableTableData((prev) => prev.map((prevItem) => ({ ...prevItem, checked: !currentChecked })));
  };

  const transfer = (from: 'assigned' | 'available') => {
    switch (from) {
      case 'assigned':
        setAssignedTableData((prevAssigned) => {
          setAvailableTableData((prevAvailable) =>
            [
              ...prevAvailable,
              ...prevAssigned.filter(({ checked }) => checked).map((item) => ({ ...item, checked: false })),
            ].sort((a, b) => (a.permissionName > b.permissionName ? 1 : -1))
          );
          return prevAssigned.filter(({ checked }) => !checked);
        });
        break;
      case 'available':
        setAvailableTableData((prevAvailable) => {
          setAssignedTableData((prevAssigned) =>
            [
              ...prevAssigned,
              ...prevAvailable.filter(({ checked }) => checked).map((item) => ({ ...item, checked: false })),
            ].sort((a, b) => (a.permissionName > b.permissionName ? 1 : -1))
          );
          return prevAvailable.filter(({ checked }) => !checked);
        });
        break;
    }
  };

  const scroll: TableProps<any>['scroll'] = {
    y: '355px',
  };

  return (
    <div className="administrationPerson-roles">
      <Row gutter={15} style={{ height: '423px' }}>
        <Col span={12}>
          <h4 className="text-bold font-size-18 mb2">Назначенные привилегии</h4>
          <Divider className="thin-divider mt0 mb1" />
          <div className="administrationPerson-roles-checkAll font-roboto" onClick={toggleCheckAssignedAll}>
            <Checkbox
              className="ml10"
              style={editMode ? {} : { visibility: 'hidden' }}
              disabled={assignedTableData.length === 0}
              checked={
                assignedTableData.length > 0 &&
                assignedTableData.filter(({ checked }) => checked).length === assignedTableData.length
              }
            />
            <div className="ml11">{assignedTableData.length} элем.</div>
            <div className="ml-auto mr10" style={editMode ? {} : { visibility: 'hidden' }}>
              Выбрать все
            </div>
          </div>
          <Table
            size="small"
            loading={checkIsLoading(userLoading)}
            showHeader={false}
            columns={columnsAssigned}
            dataSource={assignedTableData}
            className="striped-table"
            rowClassName={(r, i) => (i % 2 ? 'cursor-pointer' : 'cursor-pointer striped-table__oddRow')}
            pagination={false}
            onRow={({ id }) => ({
              onClick: () => (editMode ? toggleCheckAssigned(id) : {}),
            })}
            scroll={scroll}
          />
        </Col>
        <Col span={1} className="mt149">
          {editMode && (
            <TransferIcon
              side="right"
              active={assignedTableData.some(({ checked }) => checked)}
              onClick={() => transfer('assigned')}
            />
          )}
          {editMode && (
            <TransferIcon
              active={availableTableData.some(({ checked }) => checked)}
              onClick={() => transfer('available')}
            />
          )}
        </Col>
        <Col span={11}>
          <h4 className="text-bold font-size-18 mb2">Доступные привилегии</h4>
          <Divider className="thin-divider mt0 mb1" />
          <div
            className="administrationPerson-roles-checkAll font-roboto"
            onClick={editMode ? toggleCheckAvailableAll : () => {}}
          >
            <Checkbox
              className="ml10"
              style={editMode ? {} : { visibility: 'hidden' }}
              disabled={availableTableData.length === 0}
              checked={
                availableTableData.length > 0 &&
                availableTableData.filter(({ checked }) => checked).length === availableTableData.length
              }
            />
            <div className="ml11">{availableTableData.length} элем.</div>
            <div className="ml-auto mr10" style={editMode ? {} : { visibility: 'hidden' }}>
              Выбрать все
            </div>
          </div>
          <Table
            size="small"
            loading={checkIsLoading(allPermissionsLoading)}
            showHeader={false}
            columns={columnsAvailable}
            dataSource={availableTableData}
            className="striped-table"
            rowClassName={(r, i) => (i % 2 ? 'cursor-pointer' : 'cursor-pointer striped-table__oddRow')}
            pagination={false}
            onRow={({ id }) => ({
              onClick: () => (editMode ? toggleCheckAvailable(id) : {}),
            })}
            scroll={scroll}
          />
        </Col>
      </Row>
      <Row justify="end" gutter={20} className="mt20">
        {!editMode && hasAccess(['ADMINISTRATION.USER.WRITE']) && (
          <Col>
            <Button type="primary" onClick={() => setEditMode(true)}>
              Редактировать
            </Button>
          </Col>
        )}
        {editMode && (
          <>
            <Col>
              <Button onClick={handleDiscard}>Сбросить</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={() => {
                  onSubmitAssignPermissions({
                    include: assignedTableData.map(({ id }) => id),
                    exclude: user?.excludedPermissions.map(({ id }) => id) || [],
                  }).then(() => setEditMode(false));
                }}
              >
                Сохранить
              </Button>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};
